<template>
  <div>
    <vue-plotly
      :data="chartData"
      :layout="{ ...chartLayout, annotations: chartAnnotations }"
      :options="chartOptions"
      :config="{ responsive: true }"
      @hover="hoveredData"
    />
  </div>
</template>

<script>
import VuePlotly from "@statnett/vue-plotly";
import unitConverter from "@/utils/unitConverter";
import { mapGetters } from "vuex";

export default {
  props: ["selectedSensors", "location", "selectedAlerts", "dateInterval"],
  components: {
    VuePlotly,
  },
  data: () => ({
    chartLayout: {},
    chartOptions: {},
  }),
  created() {
    this.chartLayout = {
      showlegend: false,
      margin: {
        l: 70,
        r: 50,
        b: 50,
        t: 50,
        pad: 4,
      },
      annotations: [],
    };
  },

  computed: {
    ...mapGetters(["getMe"]),

    chartAnnotations() {
      return this.selectedAlerts
        .filter((item) => {
          return (
            this.$moment(this.$route.query.datefrom).unix() < item.created &&
            this.$moment(this.$route.query.dateto).unix() > item.created
          );
        })
        .map((item) => {
          const doesAnnotationExists = this.chartLayout.annotations.findIndex(
            (annotations) =>
              annotations.uuid === item.device_sensor.uuid &&
              annotations.created === item.created
          );

          if (
            typeof doesAnnotationExists !== "undefined" &&
            doesAnnotationExists !== -1
          ) {
            return item;
          }

          const getDataObj = this.chartData.find(
            (el) => el.name === item.device_sensor.name
          );

          if (typeof getDataObj !== "undefined" && getDataObj.x.length !== 0) {
            const nearestTime = getDataObj.x.reduce((prev, curr) => {
              return Math.abs(this.$moment(curr).unix() - item.created) <=
                Math.abs(this.$moment(prev).unix() - item.created)
                ? curr
                : prev;
            });

            const nearestTimeIndex = getDataObj.x.findIndex(
              (el) => el === nearestTime
            );

            return {
              x: this.$moment(item.created * 1000).format(),
              y: getDataObj.y[nearestTimeIndex],
              xref: "x",
              yref: "y",
              text: item.device_sensor.name + " alert",
              showarrow: true,
              arrowhead: 7,
              uuid: item.device_sensor.uuid,
              created: item.created,
              ax: 0,
              ay: -40,
            };
          }
        });
    },

    chartData() {
      const unitPreference = this.getMe.user.profile.unit_preference;

      if (
        this.location.length === 0 &&
        this.selectedSensors.data.length === 0
      ) {
        return [
          {
            type: "scatter",
            mode: "lines",
            connectgaps: false,
            name: "",
            uuid: "",
            x: [],
            y: [],
          },
        ];
      }

      const fillBlankData = (chartData) => {
        if (
          this.dateInterval.length === 0 ||
          !this.dateInterval[0] ||
          !this.dateInterval[1]
        ) {
          return chartData;
        }

        const startDate = this.$moment(this.dateInterval[0]).unix();
        const endDate = this.$moment(this.dateInterval[1]).unix();
        let dataWithMargin = chartData;

        if (this.$moment(startDate).diff(dataWithMargin[0].created) < 0) {
          //prepend data
          let currTime = startDate;
          let generatedData = [];
          while (this.$moment(currTime).diff(dataWithMargin[0].created) < 0) {
            //Generate dummy log each half hour
            generatedData.push({ created: currTime, value: null });
            currTime += 30 * 60;
          }

          dataWithMargin = [...dataWithMargin, ...generatedData];
        }

        if (
          this.$moment(endDate).diff(
            dataWithMargin[dataWithMargin.length - 1].created
          ) > 0
        ) {
          //append data
          let currTime = endDate;
          let generatedData = [];

          while (
            this.$moment(currTime).diff(
              dataWithMargin[dataWithMargin.length - 1].created
            ) > 0
          ) {
            //Generate dummy log each half hour
            generatedData.push({ created: currTime, value: null });
            currTime -= 30 * 60;
          }

          dataWithMargin = [...dataWithMargin, ...generatedData];
        }

        return dataWithMargin;
      };

      return this.selectedSensors.data.map((item, ind) => {
        let index = 0;
        let { records } = item;
        let gapArray = [];

        records.reduce((previous, current) => {
          if (
            this.$moment(current.created * 1000).diff(
              previous.created * 1000,
              "hours"
            ) > 1
          ) {
            gapArray.push({
              from: previous.created,
              to: current.created,
            });
          }

          return current;
        }, records[0]);

        if (this.location.length > 0) {
          records = this.location.map(({ created }) => {
            let currDiff = Math.abs(created - records[index].created);

            while (index < records.length - 1) {
              let nextDiff = Math.abs(created - records[index + 1].created);
              if (nextDiff > currDiff) break;

              currDiff = nextDiff;
              index = index + 1;
            }

            return {
              created,
              value: records[index].value,
            };
          });
        }

        gapArray.forEach((element) => {
          records = records.map((item) => {
            if (item.created > element.from && item.created <= element.to) {
              return { created: undefined, value: null };
            } else {
              return item;
            }
          });
        });

        const formatedDataForHoverTemplate = records.map((element) => {
          return unitConverter.format(
            element.value,
            item.sensor.measure_type,
            unitPreference,
            null,
            null,
            true
          );
        });

        records = fillBlankData(records);

        return {
          type: "scatter",
          mode: "lines",
          hovertemplate: formatedDataForHoverTemplate,
          connectgaps: false,
          name: item.name,
          uuid: item.uuid,
          line: { color: item.color },
          x: records.map((x) => this.$moment(x.created * 1000).format()),
          y: records.map((x) => x.value),
        };
      });
    },
  },
  methods: {
    hoveredData(val) {
      this.$emit("set-hovered-data", this.$moment(val.points[0].x).unix());
    },
  },
};
</script>
